var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { injectable } from 'inversify';
import { isSameDay, isSameMonth, startOfDay, endOfDay, subDays, isWeekend, getYear, eachDayOfInterval, formatISO, add, sub, closestTo, differenceInCalendarDays, differenceInBusinessDays, differenceInDays, endOfMonth, isValid, addDays, isWithinInterval, isToday, isYesterday, isThisWeek, isThisMonth, } from 'date-fns';
import { UnmarshallerService } from '../unmarshaller';
import { HttpService } from '../http';
import { FormatService } from '../format';
import { WorkCalendar, WorkScheduleCalendar } from './gen/calendar';
const DECEMBER = 11;
const NEW_YEAR_ICON_START_DAY = 10;
const NEW_YEAR_ICON_END_DAY = 14;
let Calendar = class Calendar {
    constructor(http, unmarshaller, format) {
        this.http = http;
        this.unmarshaller = unmarshaller;
        this.format = format;
        this.holidaysAndWeekends = {};
        this.holidays = {};
        this.downloading = {};
        this.workScheduleResults = {};
    }
    /**
     * Вычисляет пороговую дату: from_date (с начала дня) плюс days_count
     * рабочих, календарных или не учитывающих праздники дней
     *
     * @param options - Параметры расчета
     * @returns Пороговая дата
     */
    getThresholdDate(options) {
        const startDate = this.startOfDay(options.from_date);
        if (options.working_days_only) {
            return this.addBusinessDays(startDate, options.days_count);
        }
        if (options.ignore_holidays) {
            return this.addWithoutHolidayDays(startDate, options.days_count);
        }
        return add(startDate, { days: options.days_count });
    }
    /**
     * Загрузить календарь выходных и праздничных дней
     *
     * @param date - Дата, по которой будет вычисляться год для загружаемого календаря
     * @param id - Id сотрудника необходим, если загружается календарь для конкретного сотрудника
     */
    download() {
        return __awaiter(this, arguments, void 0, function* (date = new Date(), id) {
            const year = getYear(date);
            const defaultYear = 2021;
            if (year < defaultYear)
                return;
            try {
                if (!this.holidaysAndWeekends[year] && !this.downloading[year]) {
                    this.downloading[year] = true;
                    const result = yield this.unmarshaller.unmarshall(yield this.http.get(`/work_calendar/${year}`, id ? { employee_id: id } : {}, { withSide: false }), WorkCalendar);
                    this.holidaysAndWeekends[year] = result.dates
                        .filter(({ type }) => type === 'holiday' || type === 'weekend')
                        .reduce((acc, { date: day }) => (Object.assign(Object.assign({}, acc), { [day]: true })), {});
                    this.holidays[year] = result.dates
                        .filter(({ type }) => type === 'holiday')
                        .reduce((acc, { date: day }) => (Object.assign(Object.assign({}, acc), { [day]: true })), {});
                    this.downloading[year] = false;
                }
            }
            catch (e) {
                // proceed anyway
                this.downloading[year] = false;
            }
        });
    }
    /**
     * Загрузить график работы сотрудника на год
     * @param year - Год для загрузки графика
     * @param employeeId - ID сотрудника
     * @returns График работы или null при ошибке
     */
    downloadWorkSchedule(year, employeeId) {
        return __awaiter(this, void 0, void 0, function* () {
            const defaultYear = 2021;
            if (year < defaultYear)
                return null;
            if (this.holidaysAndWeekends[year] || this.downloading[year]) {
                return this.workScheduleResults[year] || null;
            }
            try {
                this.downloading[year] = true;
                const result = yield this.unmarshaller.unmarshall(yield this.http.get(`/work_schedule/${year}`, { employee_id: employeeId }, { withSide: false }), WorkScheduleCalendar);
                this.workScheduleResults[year] = result;
                this.holidaysAndWeekends[year] = {};
                this.holidays[year] = {};
                result.dates.forEach(({ date, is_holiday, is_workday }) => {
                    if (is_holiday) {
                        this.holidays[year][date] = true;
                    }
                    if (!is_workday) {
                        this.holidaysAndWeekends[year][date] = true;
                    }
                });
                this.downloading[year] = false;
                return result;
            }
            catch (e) {
                this.downloading[year] = false;
                return null;
            }
        });
    }
    /**
     * Получить массив праздничных дней за выбранный год
     * на основе ранее загруженных календарей
     *
     * @param year - Год, для которого получаем массив праздничных дней
     * @returns Массив дат праздничных дней
     */
    getHolidays(year) {
        return Object.keys(this.holidays[year] || {}).map((day) => this.startOfDay(new Date(day)));
    }
    /**
     * Получить массив праздничных и выходных дней за выбранный год
     * на основе ранее загруженных календарей
     *
     * @param year - Год, для которого получаем массив праздничных и выходных дней
     * @returns Массив дат праздничных и выходных дней
     */
    getHolidaysOrWeekends(year) {
        return Object.keys(this.holidaysAndWeekends[year] || {}).map((day) => this.startOfDay(new Date(day)));
    }
    /**
     * Сбросить все данные по сохранненным ранее календарям
     */
    reset() {
        this.holidaysAndWeekends = {};
        this.holidays = {};
        this.downloading = {};
        this.workScheduleResults = {};
    }
    /**
     * Преобразовать дату в формат ISO
     *
     * @param date - Дата, которая должна быть преобразована
     * @param options - Параметры для преобразования к ISO
     * @returns Строка в формате ISO
     */
    formatISO(date, options = {
        representation: 'complete',
    }) {
        return formatISO(date, options);
    }
    /**
     * Получить новую дату путем прибавления количества дней к выбранной дате
     *
     * @param date - Дата, на основе которой будет получена новая дата
     * @param amount - Количество дней
     * @returns Новая дата
     */
    add(date, amount) {
        return add(date, { days: amount });
    }
    /**
     * Получить новую дату путем прибавления количества дней к выбранной дате
     * за вычетом праздничных дней
     *
     * @param date - Дата, на основе которой будет получена новая дата
     * @param amount - Количество дней
     * @returns Новая дата
     */
    addWithoutHolidayDays(date, amount) {
        let count = 0;
        let result = date;
        while (count < amount) {
            result = add(result, { days: 1 });
            if (!this.isHoliday(result)) {
                count += 1;
            }
        }
        return result;
    }
    /**
     * Получить новую дату путем прибавления количества рабочих дней к выбранной дате
     *
     * @param date - Дата, на основе которой будет получена новая дата
     * @param amount - Количество рабочих дней
     * @returns Новая дата
     */
    addBusinessDays(date, amount) {
        let count = 0;
        let result = date;
        while (count < amount) {
            result = add(result, { days: 1 });
            if (!this.isHolidayOrWeekend(result)) {
                count += 1;
            }
        }
        return result;
    }
    /**
     * Получить новую дату путем вычитания количества дней из выбранной даты
     *
     * @param date - Дата, на основе которой будет получена новая дата
     * @param amount - Количество дней
     * @returns Новая дата
     */
    sub(date, amount) {
        return sub(date, { days: amount });
    }
    /**
     * Получить новую дату путем вычитания количества минут из выбранной даты
     *
     * @param date - Дата, на основе которой будет получена новая дата
     * @param amount - Количество минут
     * @returns Новая дата
     */
    subMinutes(date, amount) {
        return sub(date, { minutes: amount });
    }
    /**
     * Получить новую дату путем вычитания количества рабочих дней из выбранной даты
     *
     * @param date - Дата, на основе которой будет получена новая дата
     * @param amount - Количество рабочих дней
     * @returns Новая дата
     */
    subBusinessDays(date, amount) {
        let count = 0;
        let result = date;
        while (count < amount) {
            result = sub(result, { days: 1 });
            if (!this.isHolidayOrWeekend(result)) {
                count += 1;
            }
        }
        return result;
    }
    /**
     * Определить является выбранная дата выходным или праздничным днем
     * на основе ранее загруженных календарей
     *
     * @param date - Выбранная дата
     * @returns true, если дата является выходным или праздничным днем
     */
    isHolidayOrWeekend(date) {
        const year = getYear(date);
        if (this.holidaysAndWeekends[year]) {
            return !!this.holidaysAndWeekends[year][formatISO(date, { representation: 'date' })];
        }
        return isWeekend(date);
    }
    /**
     * Определить является выбранная дата праздничным днем
     * на основе ранее загруженных календарей
     *
     * @param date - Выбранная дата
     * @returns true, если дата является праздничным днем
     */
    isHoliday(date) {
        const year = getYear(date);
        if (this.holidays[year]) {
            return !!this.holidays[year][formatISO(date, { representation: 'date' })];
        }
        return false;
    }
    /**
     * Определить совпадает год, месяц и день у двух выбранных дат
     *
     * @param date1 - Дата №1
     * @param date2 - Дата №2
     * @returns true, если даты совпадают по году, месяцу и дню
     */
    isSameDay(date1, date2) {
        return isSameDay(date1, date2);
    }
    /**
     * Определить совпадает год и месяц у двух выбранных дат
     *
     * @param date1 - Дата №1
     * @param date2 - Дата №2
     * @returns true, если даты совпадают по году и месяцу
     */
    isSameMonth(date1, date2) {
        return isSameMonth(date1, date2);
    }
    /**
     * Определить количество полных дней между двумя датами
     * Дробные дни округляются до нуля
     *
     * @param date1 - Дата №1
     * @param date2 - Дата №2
     * @param options - Опции расчета
     * @returns Количество дней
     */
    differenceInDays(date1, date2, options) {
        if (options === null || options === void 0 ? void 0 : options.excludeHolidays) {
            return (differenceInDays(date1, date2) -
                this.countOfHolidaysInInterval(date2, date1));
        }
        if (options === null || options === void 0 ? void 0 : options.workingDaysOnly) {
            return (differenceInDays(date1, date2) -
                this.countOfHolidaysInInterval(date2, date1) -
                this.countOfWeekendsInInterval(date1, date2));
        }
        if (options === null || options === void 0 ? void 0 : options.excludeWorkingDays) {
            return differenceInBusinessDays(date1, date2);
        }
        return differenceInDays(date1, date2);
    }
    /**
     * Получить дату начала выбранного дня
     * Результат будет в локальной временной зоне
     *
     * @param date - Выбранная дата
     * @returns Дата начала дня
     */
    startOfDay(date) {
        return startOfDay(date);
    }
    /**
     * Получить дату конца выбранного дня
     * Результат будет в локальной временной зоне
     *
     * @param date - Выбранная дата
     * @returns Дата конца дня
     */
    endOfDay(date) {
        return endOfDay(date);
    }
    hasAvailableDatesAfter(since, intervals) {
        if (!intervals.length || intervals[0].active_to) {
            return true;
        }
        const start = startOfDay(since);
        let end = startOfDay(new Date(intervals[0].active_from));
        if (intervals.length === 1) {
            if (subDays(end, 1) <= start) {
                return false;
            }
            else {
                return true;
            }
        }
        for (let i = 0; i < intervals.length; i++) {
            const { active_from: activeFrom, active_to: activeTo } = intervals[i];
            const dateFrom = startOfDay(new Date(activeFrom));
            const dateTo = startOfDay(activeTo ? new Date(activeTo) : new Date('2100-01-01'));
            if (isSameDay(dateTo, end) || isSameDay(dateTo, subDays(end, 1))) {
                end = dateFrom;
            }
            if (subDays(end, 1) <= start) {
                return false;
            }
        }
        return true;
    }
    /**
     * Вернуть дату из массива дат, ближайшую к выбранной дате
     * Если в массиве отсутствуют валидные даты, то возвращается undefined
     *
     * @param date - Выбранная дата
     * @param dates - Массив дат
     * @returns Ближайшая дата или undefined
     */
    closestTo(date, dates) {
        return closestTo(date, dates);
    }
    /**
     * Определить количество календарных дней между двумя датами
     * Часть даты, отвечающая за время, исключяется из рассмотрения
     *
     * @param date1 - Дата №1
     * @param date2 - Дата №2
     * @returns Количество календарных дней
     */
    differenceInCalendarDays(date1, date2) {
        return differenceInCalendarDays(date1, date2);
    }
    /**
     * Определить количество дней отпуска между двумя датами
     *
     * @param date1 - Дата №1
     * @param date2 - Дата №2
     * @returns Количество дней отпуска
     */
    countOfVacationDays(date1, date2) {
        return (differenceInCalendarDays(date1, date2) +
            1 -
            this.countOfHolidaysInInterval(date2, date1));
    }
    /**
     * Входит ли дата в один из интервалов
     *
     * @param date - Выбранная дата
     * @param intervals - Массив интервалов
     * @returns true, если дата входит в любой из интервалов
     */
    isWithinIntervals(date, intervals) {
        return intervals.some((interval) => isWithinInterval(date, { start: interval[0], end: interval[1] }));
    }
    /**
     * Вернуть дату конца месяца на основе выбранной даты
     *
     * @param date - Выбранная дата
     * @returns Дата конца месяца
     */
    endOfMonth(date) {
        return endOfMonth(date);
    }
    /**
     * Определить идет Дата №1 раньше Даты №2
     *
     * @param date - Дата №1
     * @param minDate - Дата №2
     * @returns true, если date раньше minDate
     */
    isLessThan(date, minDate) {
        return date < minDate;
    }
    /**
     * Определить идет Дата №1 позже Даты №2
     *
     * @param date - Дата №1
     * @param maxDate - Дата №2
     * @returns true, если date позже maxDate
     */
    isMoreThan(date, maxDate) {
        return date > maxDate;
    }
    /**
     * Определить одинаковы даты или Дата №1 идет позже Даты №2
     * с учетом UTC
     *
     * @param date1 - Дата №1
     * @param date2 - Дата №2
     * @returns true, если date1 \>= date2
     */
    isEqualOrMore(date1, date2) {
        return date1.getTime() >= date2.getTime();
    }
    isTimeForNewYear() {
        const currentDay = new Date().getDate();
        const currentMonth = new Date().getMonth();
        if (currentMonth === 0 && currentDay <= NEW_YEAR_ICON_END_DAY) {
            return true;
        }
        if (currentMonth === DECEMBER && currentDay >= NEW_YEAR_ICON_START_DAY) {
            return true;
        }
        return false;
    }
    /**
     * Дата является валидной, если она наступает раньше даты, вычисляемой из опций
     *
     * @param date - Валидируемая дата
     * @param options - Параметры расчета
     * @returns true, если date раньше вычисленной даты
     */
    isNotWithinDays(date, options) {
        return date < this.getThresholdDate(options);
    }
    /**
     * Дата является валидной, если она наступает не раньше даты, вычисляемой из опций
     *
     * @param date - Валидируемая дата
     * @param options - Параметры расчета
     * @returns true, если date не раньше вычисленной даты
     */
    isWithinDays(date, options) {
        return this.startOfDay(date) >= this.getThresholdDate(options);
    }
    /**
     * Дата является валидной, если она попадает в любой интервал из переданных в options
     *
     * @param date - Валидируемая дата
     * @param options - Параметры с массивом интервалов
     * @returns true, если date входит в любой из интервалов
     */
    isInNotWithinIntervals(date, options) {
        return options.existing_dates.some((interval) => {
            return (date >= this.startOfDay(new Date(interval.from_date)) &&
                date <= new Date(interval.to_date));
        });
    }
    /**
     * Возвращает интервал, содержащий валидируемую дату
     *
     * @param date - Валидируемая дата
     * @param options - Параметры с массивом интервалов
     * @returns Интервал, содержащий дату, или undefined
     */
    getOverlapInterval(date, options) {
        return options.existing_dates.find((interval) => {
            return (date >= this.startOfDay(new Date(interval.from_date)) &&
                date <= new Date(interval.to_date));
        });
    }
    /**
     * Дата является валидной, если среди переданных интервалов дат есть хотя бы один, чье начало не раньше from_date,
     * но раньше валидируемой даты
     *
     * @param date - Валидируемая дата
     * @param options - Параметры с from_date и массивом интервалов
     * @returns true, если date попадает в интервал после from_date
     */
    isInNotWithinIntervalsAfterDate(date, options) {
        if (this.startOfDay(options.from_date) > date) {
            return true;
        }
        const startDays = [...options.existing_dates]
            .sort((first, second) => new Date(first.from_date) > new Date(second.from_date) ? 1 : -1)
            .map(({ from_date }) => from_date);
        const maxDate = startDays.find((day) => this.startOfDay(new Date(day)) >= this.startOfDay(options.from_date));
        if (!maxDate) {
            return false;
        }
        return date >= this.startOfDay(new Date(maxDate));
    }
    /**
     * Определить все дни из интервала являются праздничными/выходными
     *
     * @param date - Выбранная дата
     * @param options - Параметры интервала
     * @returns true, если все дни в интервале праздничные/выходные
     */
    areAllHolidaysInInterval(date, options) {
        const fromDate = this.startOfDay(options.from_date);
        if (this.startOfDay(options.from_date) > date) {
            return false;
        }
        return eachDayOfInterval({
            start: fromDate,
            end: date,
        }).every((day) => options.exclude_weekends
            ? this.isHoliday(day)
            : this.isHolidayOrWeekend(day));
    }
    /**
     * Определить количество праздничных дней между двумя датами
     * на основе ранее загруженных календарей
     *
     * @param date1 - Дата №1
     * @param date2 - Дата №2
     * @returns Количество праздничных дней
     */
    countOfHolidaysInInterval(date1, date2) {
        return eachDayOfInterval({
            start: date1 < date2 ? date1 : date2,
            end: date1 < date2 ? date2 : date1,
        }).filter((day) => this.isHoliday(day)).length;
    }
    /**
     * Определить количество выходных дней между двумя датами
     * на основе ранее загруженных календарей
     *
     * @param date1 - Дата №1
     * @param date2 - Дата №2
     * @returns Количество выходных дней
     */
    countOfWeekendsInInterval(date1, date2) {
        return (eachDayOfInterval({
            start: date1 < date2 ? date1 : date2,
            end: date1 < date2 ? date2 : date1,
        }).filter((day) => this.isHolidayOrWeekend(day)).length -
            this.countOfHolidaysInInterval(date2, date1));
    }
    /**
     * Определить является выбранная дата валидной
     *
     * @param date - Выбранная дата
     * @returns true, если дата валидна
     */
    isValidDate(date) {
        return isValid(date);
    }
    getPreviousDayIsWeekendOrHoliday(date, previousDayCount) {
        const yesterday = subDays(date, previousDayCount);
        return this.isHolidayOrWeekend(yesterday);
    }
    getNextDayIsWeekendOrHoliday(date, afterDaycount) {
        const afternoon = addDays(date, afterDaycount);
        return this.isHolidayOrWeekend(afternoon);
    }
    /**
     * Получить все дни между двумя датами
     *
     * @param start - Дата начала интервала
     * @param end - Дата конца интервала
     * @returns Массив дат
     */
    getAllDaysOfInterval(start, end) {
        return eachDayOfInterval({ start, end });
    }
    /**
     * Получить все интервалы выходных или праздничных дней
     *
     * @param range - Диапазон дат
     * @returns Строка с интервалами
     */
    getHolidaysOrWeekendsIntervals(range) {
        const intervals = [];
        range.forEach((day, index) => {
            if (this.isHolidayOrWeekend(day)) {
                const intervalsLength = intervals.length;
                if (range[index - 1] && this.isHolidayOrWeekend(range[index - 1])) {
                    intervals[intervalsLength - 1] = [
                        intervals[intervalsLength - 1][0],
                        day,
                    ];
                }
                else {
                    intervals[intervalsLength] = [day];
                }
            }
        });
        return intervals
            .map((interval) => interval
            .map((date) => this.format.toDate(date.toDateString()))
            .join(' - '))
            .join(', ');
    }
    /**
     * Дата является валидной, если она является сегодняшней
     *
     * @param date - Выбранная дата
     * @returns true, если дата сегодняшняя
     */
    isToday(date) {
        return isToday(new Date(date));
    }
    /**
     * Дата является валидной, если она является вчерашней
     *
     * @param date - Выбранная дата
     * @returns true, если дата вчерашняя
     */
    isYesterday(date) {
        return isYesterday(new Date(date));
    }
    /**
     * Дата является валидной, если она входит в текущую неделю
     *
     * @param date - Выбранная дата
     * @param weekStartsOn - День недели, с которого начинается неделя
     * @returns true, если дата в текущей неделе
     */
    // eslint-disable-next-line no-magic-numbers
    isThisWeek(date, weekStartsOn) {
        return isThisWeek(new Date(date), { weekStartsOn });
    }
    /**
     * Дата является валидной, если она входит в текущий месяц
     *
     * @param date - Выбранная дата
     * @returns true, если дата в текущем месяце
     */
    isThisMonth(date) {
        return isThisMonth(new Date(date));
    }
};
Calendar.minDate = new Date('1900-01-01');
Calendar.maxDate = new Date('2100-01-01');
Calendar = __decorate([
    injectable(),
    __metadata("design:paramtypes", [HttpService,
        UnmarshallerService,
        FormatService])
], Calendar);
export { Calendar };
